import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { cn } from 'components/banff-ui/cn';

const PopoverPortalContainerContext = createContext<HTMLElement | null>(null);
const SetPopoverPortalContainerContext = createContext<
  ((container: HTMLElement | null) => void) | null
>(null);

/**
 * Floating panel tied to a trigger.
 * Use for content that is not a command list: a preview, a short form, or filter controls.
 * A list of commands is a `DropdownMenu`.
 */
export function Popover<Payload = unknown>(
  props: PopoverPrimitive.Root.Props<Payload>,
) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  return (
    <SetPopoverPortalContainerContext.Provider value={setContainer}>
      <PopoverPortalContainerContext.Provider value={container}>
        <PopoverPrimitive.Root data-slot="popover" {...props} />
      </PopoverPortalContainerContext.Provider>
    </SetPopoverPortalContainerContext.Provider>
  );
}

/** Element that opens the popover. */
export function PopoverTrigger<Payload = unknown>(
  props: PopoverPrimitive.Trigger.Props<Payload>,
) {
  const setContainer = useContext(SetPopoverPortalContainerContext);
  const ref = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    // Tokens and utilities only apply inside `.banff-ui`, so the popup must portal there.
    setContainer?.(ref.current?.closest<HTMLElement>('.banff-ui') ?? null);
  }, [setContainer]);

  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      {...props}
      ref={ref}
    />
  );
}

export interface PopoverContentProps
  extends
    PopoverPrimitive.Popup.Props,
    Pick<
      PopoverPrimitive.Positioner.Props,
      | 'align'
      | 'alignOffset'
      | 'collisionAvoidance'
      | 'collisionBoundary'
      | 'collisionPadding'
      | 'side'
      | 'sideOffset'
    > {}

/**
 * Panel anchored to `PopoverTrigger`.
 * To keep it on its side and fit it to a container, pass `collisionAvoidance` and
 * `collisionBoundary`, then cap its height with `max-h-[var(--available-height)]`.
 */
export function PopoverContent({
  align = 'center',
  alignOffset = 0,
  className,
  collisionAvoidance,
  collisionBoundary,
  collisionPadding,
  side = 'bottom',
  sideOffset = 4,
  ...props
}: PopoverContentProps) {
  const container = useContext(PopoverPortalContainerContext);

  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="isolate z-[110]"
        collisionAvoidance={collisionAvoidance}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          {...props}
          className={cn(
            'bg-popover text-popover-foreground ring-foreground/10 z-[110] flex w-72 origin-[var(--transform-origin)] flex-col gap-2.5 rounded-lg p-2.5 text-sm shadow-md outline-none ring-1 transition-[opacity,transform] duration-100 data-[ending-style]:scale-95 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            className,
          )}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

/** Title row inside the popover. */
export function PopoverHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      {...props}
      className={cn('flex flex-col gap-0.5 text-sm', className)}
    />
  );
}

/** Popover heading. */
export function PopoverTitle({
  className,
  ...props
}: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      {...props}
      className={cn('font-medium', className)}
    />
  );
}

/** Supporting text under the popover title. */
export function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      {...props}
      className={cn('text-muted-foreground', className)}
    />
  );
}
